/**
 * RiderPillNav — tab bar flottante del biker su mobile.
 *
 * Estratto da Navbar.tsx il 26 Ott 2026 (punto 7: refactor dei monoliti).
 * Comportamento invariato: 2 voci a sinistra, CTA "Inizia Giro" al centro,
 * 2 voci a destra (simmetria del 21 Ott), sheet notifiche al tap sull'omino.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Calendar, Home, Map, UserRound, Zap } from "lucide-react";
import { tapFeedback } from "@/utils/vibration";
import NotifQuickSheet from "@/components/nav/NotifQuickSheet";
import useIsActive from "@/components/nav/useIsActive";

export interface RiderPillNavProps {
  dmUnread: number;
  totalNotifUnread: number;
  connectionRequests: number;
  onLogout: () => void;
}

export default function RiderPillNav({
  dmUnread,
  totalNotifUnread,
  connectionRequests,
  onLogout,
}: RiderPillNavProps) {
  const isActive = useIsActive();
  const totalBadge = dmUnread + totalNotifUnread;
  const [sheetOpen, setSheetOpen] = useState(false);

  const location = useLocation();
  useEffect(() => {
    setSheetOpen(false);
  }, [location.pathname]);

  function PillItem({
    to,
    icon,
    label,
    badge,
  }: {
    to: string;
    icon: React.ReactNode;
    label: string;
    badge?: number;
  }) {
    const active = isActive(to);
    return (
      <Link
        to={to}
        className="relative flex flex-col items-center justify-center w-12 h-12 group"
        aria-label={label}
      >
        <div
          className={`transition-all duration-300 ${
            active
              ? "text-white scale-110"
              : "text-neutral-500 group-hover:text-neutral-300"
          }`}
        >
          {icon}
        </div>
        {active && (
          <span className="absolute -bottom-2 w-1 h-1 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.9)]" />
        )}
        {badge && badge > 0 ? (
          <span className="absolute top-0 right-0 w-4 h-4 bg-brand-500 text-black text-[9px] font-extrabold flex items-center justify-center rounded-full shadow-neon">
            {badge > 9 ? "9+" : badge}
          </span>
        ) : null}
      </Link>
    );
  }

  return (
    <>
      {sheetOpen && (
        <NotifQuickSheet
          dmUnread={dmUnread}
          notifUnread={totalNotifUnread}
          connectionRequests={connectionRequests}
          onClose={() => setSheetOpen(false)}
          onLogout={onLogout}
        />
      )}

      {/* ── Adaptive Floating Tab Bar ── */}
      {/* FIX SAFE-AREA (02 Set 2026): la barra flotta SOPRA la gesture bar
          (bottom = 1rem + inset di sistema) e il wrapper non intercetta i
          tap nell'area trasparente (pointer-events: none). Prima il wrapper
          copriva i pulsanti di sistema e le zone tappabili in basso. */}
      <div
        className="mobile-bottom-nav md:hidden fixed left-3 right-3 z-50 animate-spring-up pointer-events-none"
        style={{
          bottom:
            "calc(1rem + env(safe-area-inset-bottom, var(--safe-area-inset-bottom, 0px)))",
        }}
      >
        <nav className="pointer-events-auto mx-auto max-w-lg bg-gray-950/75 backdrop-blur-2xl border border-white/8 rounded-[28px] shadow-[0_16px_48px_rgba(0,0,0,0.7),0_0_0_1px_rgba(255,255,255,0.04)_inset] px-2 py-2.5 grid grid-cols-[1fr_1fr_auto_1fr_1fr] items-center">
          {/* SIMMETRIA (21 Ott 2026): 2 voci a sinistra e 2 a destra della CTA.
              Prima erano 2 + CTA + 3, quindi il pulsante arancione non era più
              al centro (segnalato: «due tasti a sx e tre a dx»). Con quattro
              colonne 1fr uguali e la CTA in una colonna `auto` il fulmine sta
              esattamente al centro della barra. */}
          <div className="flex justify-center">
            <PillItem to="/dashboard" icon={<Home size={22} />} label="Home" />
          </div>
          {/* UX (Ott 2026): "Esplora" diventa "Percorsi", lo stesso nome che
              l'utente trova nel menu: prima la stessa sezione aveva due nomi
              diversi a seconda di dove la si apriva. */}
          <div className="flex justify-center">
            <PillItem to="/routes" icon={<Map size={22} />} label="Percorsi" />
          </div>

          {/* ⚡ CTA centrale — Inizia Giro (one-tap, autoStart) */}
          <div className="relative -mt-10 px-1">
            <Link
              to="/routes/record?autoStart=1"
              onClick={tapFeedback}
              aria-label="Inizia un giro — registrazione automatica"
              className="relative z-10 p-2 -m-2 touch-target flex items-center justify-center"
              title="Inizia Giro"
            >
              <div className="w-[56px] h-[56px] rounded-full bg-gradient-to-br from-brand-500 to-brand-600 text-white shadow-[0_6px_24px_rgba(255,90,31,0.45)] hover:shadow-[0_8px_32px_rgba(255,90,31,0.6)] active:scale-90 transition-all duration-300 flex items-center justify-center">
                <Zap size={22} fill="currentColor" />
              </div>
            </Link>
          </div>

          {/* UX (Ott 2026): gli Eventi sono un pilastro del prodotto ma dalla
              barra non erano raggiungibili: si potevano aprire solo dal menu. */}
          <div className="flex justify-center">
            <PillItem to="/events" icon={<Calendar size={22} />} label="Eventi" />
          </div>

          {/* Menu utente / notifiche.
              "Vicini" (/nearby) è uscito dalla barra per lasciare la CTA al
              centro: la voce resta nel menu, dove c'è già con lo stesso nome. */}
          <div className="relative flex justify-center">
            <div className="relative flex flex-col items-center justify-center w-[44px] h-[44px] group">
            <button
              onClick={() => setSheetOpen(true)}
              aria-label="Menu utente"
              aria-expanded={sheetOpen}
              className={`transition-all duration-300 ${
                // FEAT MENU-ACTIVE (18 Set 2026): l'omino si illumina e il
                // puntino si sposta qui quando il menu è APERTO (prima il
                // puntino restava sull'ultima tab, es. Esplora/Radar, anche
                // con il menu utente aperto).
                isActive("/profile") || sheetOpen
                  ? "text-white scale-110"
                  : "text-neutral-500 group-hover:text-neutral-300"
              }`}
            >
              <UserRound size={22} />
            </button>

            {(isActive("/profile") || sheetOpen) && (
              <span className="absolute -bottom-2 w-1 h-1 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.9)]" />
            )}

            {totalBadge > 0 && (
              <button
                onClick={() => setSheetOpen(true)}
                aria-label={`${totalBadge} notifiche non lette — tocca per vedere`}
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-brand-500 text-white text-[9px] font-extrabold flex items-center justify-center rounded-full shadow-[0_2px_8px_rgba(255,90,31,0.5)] active:scale-90 transition-transform animate-pulse"
              >
                {totalBadge > 9 ? "9+" : totalBadge}
              </button>
            )}
            </div>
          </div>
        </nav>
      </div>
    </>
  );
}
